import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { DialogTrigger } from "react-aria-components";
import { Button, DropdownWithSubLabelButton } from "@/components/ui/button";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton";
import { Checkbox } from "@/components/ui/forms/checkbox";
import { MenuSeparator } from "@/components/ui/menu";
import SearchBar from "@/components/ui/molecules/FilterSearchBar";
import { Popover } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface KMItem {
  id: string;
  name: string;
}

interface KMGroup {
  id: string;
  name: string;
  items: KMItem[];
}

interface KeyMessagesListProps {
  groups: KMGroup[];
  isFetching: boolean;
  /** Error message from a failed fetch. When set, shows an error state with a retry button. */
  error?: string | null;
  /** Called when the user clicks the retry button in the error state. */
  onRetry?: () => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  defaultLabel: string;
  /** Optional class name forwarded to the trigger button (e.g. for feature-flag colour overrides). */
  triggerClassName?: string;
}

function getButtonLabel(selectedIds: string[], defaultLabel: string): string {
  if (selectedIds.length === 0) return defaultLabel;
  return `${selectedIds.length} key topic${selectedIds.length === 1 ? "" : "s"} selected`;
}

interface GroupRowProps {
  group: KMGroup;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  searchTerm: string;
}

const GroupRow = ({
  group,
  selectedIds,
  onSelectionChange,
  searchTerm,
}: GroupRowProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const filteredItems = searchTerm
    ? group.items.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : group.items;

  const selectedSet = new Set(selectedIds);
  const groupItemIds = group.items.map((i) => i.id);
  const selectedInGroup = groupItemIds.filter((id) => selectedSet.has(id));
  const allSelected =
    groupItemIds.length > 0 && selectedInGroup.length === groupItemIds.length;

  const handleGroupCheckbox = () => {
    if (allSelected) {
      const next = selectedIds.filter((id) => !groupItemIds.includes(id));
      onSelectionChange(next);
    } else {
      const next = Array.from(new Set([...selectedIds, ...groupItemIds]));
      onSelectionChange(next);
    }
  };

  const handleItemCheckbox = (itemId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange(Array.from(new Set([...selectedIds, itemId])));
    } else {
      onSelectionChange(selectedIds.filter((id) => id !== itemId));
    }
  };

  if (searchTerm && filteredItems.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-1 px-1.5 py-1 rounded-xs hover:bg-slate-50">
        <Button
          variant="none"
          size="none"
          className="flex items-center justify-start flex-1 gap-1.5 min-w-0 text-left font-normal"
          onPress={handleGroupCheckbox}
          aria-label={`Toggle all items in ${group.name}`}
        >
          <Checkbox
            id={`group-${group.id}`}
            checked={allSelected}
            isSelectAll
            onCheckedChange={() => handleGroupCheckbox()}
            onClick={(e) => e.stopPropagation()}
            className="cursor-pointer"
          />
          <span className="text-xs text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap font-medium">
            {group.name}
          </span>
        </Button>
        <Button
          variant="none"
          size="none"
          aria-label={isExpanded ? "Collapse group" : "Expand group"}
          className="shrink-0 text-gray-400 hover:text-gray-700 transition-colors p-0.5"
          onPress={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? (
            <CaretUpIcon size={12} aria-hidden />
          ) : (
            <CaretDownIcon size={12} aria-hidden />
          )}
        </Button>
      </div>

      {isExpanded && (
        <div className="pl-4">
          {filteredItems.map((item) => (
            <label
              key={item.id}
              htmlFor={`item-${item.id}`}
              className="flex items-center gap-1.5 px-1.5 py-1 rounded-xs hover:bg-slate-50 cursor-pointer"
            >
              <Checkbox
                id={`item-${item.id}`}
                checked={selectedSet.has(item.id)}
                className="cursor-pointer"
                onCheckedChange={(checked) =>
                  handleItemCheckbox(item.id, checked === true)
                }
              />
              <span className="text-xs text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
                {item.name}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const KeyMessagesList = ({
  groups,
  isFetching,
  error,
  onRetry,
  selectedIds,
  onSelectionChange,
  defaultLabel,
  triggerClassName,
}: KeyMessagesListProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);

  const allItemIds = groups.flatMap((g) => g.items.map((i) => i.id));
  const lowerSearch = searchTerm.toLowerCase();
  const hasMatches =
    !!searchTerm &&
    groups.some((g) =>
      g.items.some((i) => i.name.toLowerCase().includes(lowerSearch)),
    );

  const handleSelectAll = () => {
    onSelectionChange(
      selectedIds.length === allItemIds.length ? [] : allItemIds,
    );
  };

  const buttonLabel = getButtonLabel(selectedIds, defaultLabel);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <DropdownWithSubLabelButton
        ref={triggerRef}
        ariaLabel="key-messages-selector-button"
        buttonTitle="Key Topics"
        label={buttonLabel}
        className={cn(
          "flex-initial min-w-48 max-w-[400px] p-3 items-center h-9 py-2 border bg-white",
          triggerClassName,
        )}
        buttonSize="lg"
      />
      <Popover
        triggerRef={triggerRef}
        placement="bottom start"
        className="w-[400px] bg-white border border-gray-200 rounded-md shadow-md z-50 outline-none"
        // Keep popover open when interacting with portalled checkboxes.
        shouldCloseOnInteractOutside={(element) =>
          !element.closest("[data-key-messages-popover]")
        }
      >
        <div data-key-messages-popover className="flex flex-col max-h-[400px]">
          <div className="px-1 pt-1 pb-0">
            <SearchBar
              searchTerm={searchTerm}
              title="key topics"
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
              isOpen={isOpen}
            />
          </div>

          {!searchTerm && (
            <div className="flex items-center justify-between px-2.5 py-1.5">
              <Button
                variant="none"
                size="none"
                className="flex items-center gap-1.5 font-normal"
                onPress={handleSelectAll}
              >
                <Checkbox
                  checked={
                    allItemIds.length > 0 &&
                    selectedIds.length === allItemIds.length
                  }
                  onCheckedChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                  className="cursor-pointer"
                />
                <span className="text-xs text-blue-600 font-medium no-underline">
                  Select all
                </span>
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  variant="none"
                  size="none"
                  className="text-xs text-blue-600 font-medium"
                  onPress={() => onSelectionChange([])}
                >
                  Deselect all
                </Button>
              )}
            </div>
          )}

          <MenuSeparator className="mx-1" />

          {/* Groups list */}
          <div className="overflow-y-auto flex-1 px-1 pt-1 pb-2">
            {isFetching ? (
              <div className="space-y-1 px-1.5 py-1">
                <ItemSkeleton />
                <ItemSkeleton />
                <ItemSkeleton />
              </div>
            ) : error ? (
              <div className="flex flex-col items-start gap-1.5 px-2 py-2">
                <p className="text-xs text-red-600">{error}</p>
                {onRetry && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onPress={onRetry}
                  >
                    Retry
                  </Button>
                )}
              </div>
            ) : groups.length === 0 ? (
              <p className="text-xs text-gray-400 px-2 py-2">
                No key topics available.
              </p>
            ) : searchTerm && !hasMatches ? (
              <p className="text-xs text-gray-400 px-2 py-2">
                No key topics found matching &ldquo;{searchTerm}&rdquo;
              </p>
            ) : (
              groups.map((group) => (
                <GroupRow
                  key={group.id}
                  group={group}
                  selectedIds={selectedIds}
                  onSelectionChange={onSelectionChange}
                  searchTerm={searchTerm}
                />
              ))
            )}
          </div>
        </div>
      </Popover>
    </DialogTrigger>
  );
};

export default KeyMessagesList;
